import { Injectable, inject } from '@angular/core';
import { State, Selector, Action, StateContext } from '@ngxs/store';
import { tap, finalize, map, Observable, forkJoin } from 'rxjs';
import { IAuditEntry } from '../../interfaces/audit/IAudit';
import { AuditService } from '../../services/audit.service';
import { LoadAuditHistory, LoadRelatedAuditHistory, LoadCompositeAuditHistory, ClearAuditHistory } from './audit.actions';

export interface IAuditStateModel {
  auditHistory: IAuditEntry[];
  isLoading: boolean;
  selectedEntity: { entityName: string; entityId: string } | null;
}

@State<IAuditStateModel>({
  name: 'auditState',
  defaults: {
    auditHistory: [],
    isLoading: false,
    selectedEntity: null,
  },
})
@Injectable()
export class AuditState {
  private auditService = inject(AuditService);

  @Selector()
  static auditHistory(state: IAuditStateModel): IAuditEntry[] {
    return state.auditHistory;
  }

  @Selector()
  static isLoading(state: IAuditStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static selectedEntity(state: IAuditStateModel): { entityName: string; entityId: string } | null {
    return state.selectedEntity;
  }

  @Action(LoadAuditHistory)
  loadAuditHistory(ctx: StateContext<IAuditStateModel>, action: LoadAuditHistory): Observable<IAuditEntry[]> {
    this.setIsLoading(ctx, true);
    return this.auditService.getAuditHistory(action.entityName, action.entityId).pipe(
      tap(auditHistory =>
        ctx.patchState({
          auditHistory,
          selectedEntity: { entityName: action.entityName, entityId: action.entityId },
        }),
      ),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(LoadRelatedAuditHistory)
  loadRelatedAuditHistory(ctx: StateContext<IAuditStateModel>, action: LoadRelatedAuditHistory): Observable<IAuditEntry[]> {
    this.setIsLoading(ctx, true);
    return this.auditService.getRelatedAuditHistory(action.entityType, action.relation, action.value).pipe(
      map(relatedHistories => {
        const flatEntries = Object.values(relatedHistories).flat();
        return flatEntries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      }),
      tap(auditHistory => ctx.patchState({ auditHistory })),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(LoadCompositeAuditHistory)
  loadCompositeAuditHistory(ctx: StateContext<IAuditStateModel>, action: LoadCompositeAuditHistory): Observable<IAuditEntry[]> {
    this.setIsLoading(ctx, true);

    const mainRequest$ = this.auditService
      .getAuditHistory(action.mainEntityName, action.entityId)
      .pipe(map(entries => entries.map(entry => ({ ...entry, entityType: action.mainEntityName }))));

    const relatedRequests$ = action.relatedEntities.map(related =>
      this.auditService.getRelatedAuditHistory(related.entityName, related.relationKey, action.entityId).pipe(
        map(relatedHistories => {
          const flatEntries = Object.values(relatedHistories).flat();
          return flatEntries.map(entry => ({ ...entry, entityType: related.entityName }));
        }),
      ),
    );

    return forkJoin([mainRequest$, ...relatedRequests$]).pipe(
      map(results => {
        const allEntries = results.flat();
        return allEntries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      }),
      tap(auditHistory =>
        ctx.patchState({
          auditHistory,
          selectedEntity: { entityName: action.mainEntityName, entityId: action.entityId },
        }),
      ),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(ClearAuditHistory)
  clearAuditHistory(ctx: StateContext<IAuditStateModel>): void {
    ctx.patchState({
      auditHistory: [],
      selectedEntity: null,
    });
  }

  private setIsLoading(ctx: StateContext<IAuditStateModel>, isLoading: boolean): void {
    ctx.patchState({ isLoading });
  }
}
